<template>
  <div
    ref="end-chat-wrapper"
    class="end-chat-wrapper"
  >
    <div
      v-focus-trap
      class="end-chat-container"
      :class="{ 'end-chat-container-compact': isMobileScreenOrCompactWidget }"
    >
      <div
        v-if="!isMobileScreenOrCompactWidget"
        class="close-icon-container"
        @click="handleClose(false, true)"
      >
        <BaseIcon
          name="CloseIcon"
          :fill="isDownloadActive ? '#FFFFFF' : '#6D6D6D'"
        />
      </div>
      <p
        v-t="endChat"
        class="end-chat-text"
        :style="{ ...getFontSizeForChatInterface('title') }"
        :class="{ 'end-chat-text-compact': isMobileScreenOrCompactWidget }"
      />
      <p
        v-t="endChatDiscalimer"
        class="disclaimer-text"
        :class="{ 'disclaimer-text-compact': isMobileScreenOrCompactWidget }"
        :style="{ ...getFontSizeForChatInterface('bodyText') }"
      />
      <div
        v-if="isSendTranscriptEnabled || isDownloadTranscriptEnabled"
        :style="{ ...getFontSizeForChatInterface('bodyText') }"
        class="transcript-outer-container"
        :class="{
          'transcript-container-style': !isMobileScreenOrCompactWidget
        }"
      >
        <p
          v-t="getTranscriptLabel"
          class="transcript-container-header"
        />
        <div class="transcript-container-content">
          <div
            v-if="isSendTranscriptEnabled"
            class="send-transcript-inner-container"
          >
            <div class="send-transcript-container-content">
              <CustomCheckbox
                :checked="sendTranscriptCheckbox"
                :label="endChatLabel"
                :handleCheckbox="handleCheckbox"
                :isDisabled="isDownloadActive"
                :index="0"
              />
            </div>
          </div>
          <div
            v-if="isDownloadTranscriptEnabled"
            class="download-transcript-outer-container"
          >
            <button
              ref="submitButton"
              class="download-transcript-btn"
              :class="{
                'download-transcript-btn-disabled': !isDownloadInactive
              }"
              @click="handleDownload"
            >
              <BaseIcon
                :name="getDownloadIcon"
                :class="{
                  'submit-icon': isDownloadCompleted,
                  'inactive-icon': isDownloadInactive,
                  'active-icon': isDownloadActive
                }"
              />
              <p
                :style="{ ...getFontSizeForChatInterface('bodyText') }"
                :class="{ 'inactive-text': isDownloadInactive }"
              >
                {{ getTextForDownloadButton }}
              </p>
            </button>
          </div>
        </div>
        <div
          v-if="sendTranscriptCheckbox"
          class="custom-input-outer-wrapper"
        >
          <CustomInput
            :showRequiredSign="false"
            :placeholder="getEmailPlaceholder"
            :label="getEmailAddressLabel"
            labelFor="email"
            :value="email"
            :handleFormInput="handleFormInput"
            :handleOnKeyUp="handleOnKeyUp"
            :isInvalid="error.errorState"
            :showLabel="false"
            :isDisabled="isDownloadActive"
          />
          <InputError
            v-if="error.errorState"
            :errorMessage="error.errorMessage"
          />
        </div>
      </div>
      <div
        :style="{ ...getFontSizeForChatInterface('bodyText') }"
        class="buttons-outer-container"
        :class="{
          'buttons-outer-container-compact': isMobileScreenOrCompactWidget
        }"
      >
        <button
          v-t="getCancelBtnCTA"
          class="tab-focused-btn btn cancel-btn"
          :class="{
            'cancel-btn-compact': isMobileScreenOrCompactWidget,
            'btn-disabled': isDownloadActive
          }"
          @click="handleClose(false, true)"
        />
        <button
          ref="confirm-btn"
          v-t="getConfirmBtnCTA"
          :disabled="isConfirmDisabled()"
          :class="{
            'btn-disabled': isConfirmDisabled() || isDownloadActive,
            'confirm-btn-compact': isMobileScreenOrCompactWidget
          }"
          class="tab-focused-btn btn confirm-btn"
          @click="handleEndChat"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/views/BaseIcon.vue'
import CustomCheckbox from './CustomCheckbox'
import CustomInput from './CustomInput'
import InputError from './InputError'
import validators from '@/services/validators'
import AppWarn from '@/services/errors/app-warns'
import { constantStrings, jsonConfigTemplate, customAttributes } from '@/services/constants'
import {
  getTranslation,
  hasConversationInitiated,
  isEndChatSuveyValid,
  isEndChatGreetingValid,
  getDeviceType,
  isTypeBOT
} from '@/services/utils'
import { generateTranscript } from '@/services/liveAgent/messageFormatter/chatTranscriptFormatter'
import { chatEvents } from '@/services/constants'
import {
  emitEventToParentWindow,
  getEventDataPoints
} from '../../../../services/utils'
import { eventType } from '../../../../services/constants'
import axios from 'axios'
import fontSizeMixin from '@/mixins/fontSizeMixin'
import { GlobalVars } from '@/services/globals'

export default {
  mixins: [fontSizeMixin],
  // eslint-disable-next-line vue/order-in-components
  components: {
    CustomCheckbox,
    CustomInput,
    InputError,
    BaseIcon
  },
  data() {
    return {
      sendTranscriptCheckbox: false,
      email: '',
      downloadStatus: constantStrings.INACTIVE,
      error: {
        errorState: false,
        errorMessage: ''
      }
    }
  },
  computed: {
    getTriggerTopicAtEndChat () {
      return this.$store?.state?.widgetConfig?.endChat?.triggerTopicAtEndChat
    },
    endChatClientConfigsEnabled() {
      return this.$store.state?.clientConfigs?.endChat?.enabled
    },
    endChatClientConfigs() {
      if (!this.endChatClientConfigsEnabled) return null
      return this.$store.state?.clientConfigs?.endChat?.config
    },
    isDownloadInactive() {
      return this.downloadStatus === constantStrings.INACTIVE
    },
    isDownloadActive() {
      return this.downloadStatus === constantStrings.ACTIVE
    },
    isDownloadCompleted() {
      return this.downloadStatus === constantStrings.COMPLETED
    },
    getDownloadIcon() {
      if (this.isDownloadCompleted) {
        return 'FormSubmittedIcon'
      } else if (this.isDownloadActive) {
        return 'LoaderIcon'
      }
      return 'FileDownload'
    },
    getTextForDownloadButton() {
      if (this.isDownloadCompleted) {
        return getTranslation(constantStrings.DOWNLOADED)
      } else if (this.isDownloadActive) {
        return getTranslation(constantStrings.DOWNLOADING)
      }
      return getTranslation(constantStrings.DOWNLOAD)
    },
    isDownloadTranscriptEnabled() {
      if (this.getEndChatModalOnChatRestart) {
        return !!this.$store.state.widgetConfig?.restartChat?.showWarning
          ?.downloadTranscript
      }
      if (this.endChatClientConfigs?.downloadTranscript !== undefined) {
        return this.endChatClientConfigs?.downloadTranscript
      }

      return this.$store.state.widgetConfig.endChat?.downloadTranscript
    },
    isMobileScreenOrCompactWidget() {
      return (
        getDeviceType() === 'mobile' ||
        !this.$store.state.isChatWidgetFullScreen
      )
    },
    getEmailPlaceholder() {
      return getTranslation(constantStrings.ENTER_EMAIL_ID)
    },
    getEmailAddressLabel() {
      return getTranslation(constantStrings.EMAIL_ADDRESS_LABEL)
    },
    endChat() {
      if (this.getEndChatModalOnChatRestart)
        return constantStrings.RESTART_PRIMARY_BTN_TEXT
      if (this.endChatClientConfigs?.endChatButtonCTA) {
        return this.endChatClientConfigs?.endChatButtonCTA
      }

      return jsonConfigTemplate.END_CHAT_BUTTON_CTA
    },
    endChatDiscalimer() {
      if (this.getEndChatModalOnChatRestart)
        return jsonConfigTemplate.RESTART_CHAT_DESCRIPTION
      if (this.endChatClientConfigs?.endChatDisclaimer) {
        return this.endChatClientConfigs?.endChatDisclaimer
      }

      return constantStrings.END_CHAT_DISCLAIMER
    },
    getCancelBtnCTA() {
      if (this.getEndChatModalOnChatRestart)
        return jsonConfigTemplate.RESTART_CHAT_WARNING_SECONDARY_CTA

      if (this.endChatClientConfigs?.endChatCancelButtonCTA) {
        return this.endChatClientConfigs?.endChatCancelButtonCTA
      }
      return constantStrings.END_CHAT_CANCEL_BUTTON_CTA
    },
    getConfirmBtnCTA() {
      if (this.getEndChatModalOnChatRestart)
        return jsonConfigTemplate.RESTART_CHAT_WARNING_PRIMARY_CTA

      if (this.endChatClientConfigs?.endChatConfirmButtonCTA) {
        return this.endChatClientConfigs?.endChatConfirmButtonCTA
      }
      return constantStrings.END_CHAT_CONFIRM_BUTTON_CTA
    },
    isSendTranscriptEnabled() {
      if (this.getEndChatModalOnChatRestart) {
        return !!this.$store.state.widgetConfig?.restartChat?.showWarning
          ?.isSendTranscriptEnabled
      }

      if (this.endChatClientConfigs?.isSendTranscriptEnabled !== undefined) {
        return this.endChatClientConfigs?.isSendTranscriptEnabled
      }

      return this.$store.state.widgetConfig.endChat
        ? this.$store.state.widgetConfig?.endChat?.isSendTranscriptEnabled
        : true
    },
    endChatLabel() {
      if (this.endChatClientConfigs?.sendTranscriptCTA) {
        return this.endChatClientConfigs?.sendTranscriptCTA
      }
      return (
        getTranslation(jsonConfigTemplate.END_CHAT_SEND_TRANSCRIPT_CTA) ||
        'Send to email'
      )
    },
    endChatQuery() {
      return this.$store.state.widgetConfig?.endChat?.endChatQuery || 'End Chat'
    },
    userEmail() {
      return this.$store.state.user.email
    },
    getEndChatModalOnChatRestart() {
      return this.$store.state?.showEndChatFlow?.isChatRestart
    },
    getTranscriptLabel() {
      if (this.endChatClientConfigs?.transcriptLabel) {
        return this.endChatClientConfigs?.transcriptLabel
      }
      return constantStrings.TRANSCRIPT_LABEL
    },
    isEventAnalyticsMP() {
      return this.$store.state.widgetConfig?.eventAnalyticsMP || false
    }
  },
  created() {
    this.email = this.userEmail || ''
    this.constantStrings = constantStrings
  },
  methods: {
    isConfirmDisabled() {
      return !hasConversationInitiated() || !this.isEmailValid()
    },
    isEmailValid() {
      return !(
        this.sendTranscriptCheckbox &&
        (!this.email || this.error.errorState)
      )
    },
    handleCheckbox(value) {
      this.sendTranscriptCheckbox = value
    },
    handleFormInput(value) {
      this.email = value
    },
    handleOnKeyUp() {
      if (validators.isEmail(this.email)) {
        this.resetError()
      } else {
        this.error = {
          errorState: true,
          errorMessage: getTranslation(constantStrings.MAIL_VALIDATION_MESSAGE)
        }
      }
    },
    async handleEndChat() {
      try {
        this.handleOnKeyUp()
        const triggerTopicAtEndChat = this.getTriggerTopicAtEndChat

        // Send Transcript Logic
        if (this.sendTranscriptCheckbox && !this.error.errorState) {
          this.isEventAnalyticsMP &&
            this.$segment.global.sendEvent('end_chat_send_transcript', {
              email: this.email
            })
          this.$store.dispatch('updateUserEmail', this.email)
          if (!this.getEndChatModalOnChatRestart && !triggerTopicAtEndChat?.length) {
            await this.$store.dispatch('sendMessage', {
              userMessage: {
                payload: getTranslation(jsonConfigTemplate.END_CHAT_QUERY)
              },
              hide: true
            })
          }
          if (this.$store?.state?.widgetConfig?.sendTranscriptEmailSetup?.enable) {
            if (triggerTopicAtEndChat?.length) {
              this.$store.dispatch('transcriptMessage').catch((err) => {
                console.log(err?.message || err)
                new AppWarn({
                  name: 'EndChat',
                  message: `Error while sending transcript. ${err?.message || err}`
                })
              })
            } else {
              await this.$store.dispatch('transcriptMessage')
            }
          }
        }

        this.isEventAnalyticsMP &&
          this.$segment.global.sendEvent('end_chat_button_click')

        if (triggerTopicAtEndChat?.length && !this.getEndChatModalOnChatRestart) {
					const isControlTypeBot = isTypeBOT(this.$store.state.controlType)
					if (!isControlTypeBot) {
						const triggerResponseAttribute = customAttributes.triggerResponse()
						await GlobalVars.$liveAgent.endChatWithLiveAgent(triggerResponseAttribute)
					}
					this.$store.dispatch('sendMessage', {
						userMessage: {
							payload: triggerTopicAtEndChat,
							label: triggerTopicAtEndChat,
							hideMessage: true
						}
					})
        } else {
					const isEndChatSurveyTriggered = isEndChatSuveyValid()
					const isMinimizeValid =
						!isEndChatSurveyTriggered &&
						!isEndChatGreetingValid(this.isSendTranscriptEnabled) &&
						!this.getEndChatModalOnChatRestart
					isEndChatSurveyTriggered &&
						this.$store.dispatch('updateTriggerMinimizeValidState', true)
					// End Chat Logic
					await this.$store.dispatch('endChat', {
						message: this.sendTranscriptCheckbox
							? constantStrings.CHAT_ENDED_WITH_TRANSCRIPT
							: constantStrings.CHAT_ENDED,
						showSessionRestartGreeting: true,
						hideEndChatGreeting: this.isSendTranscriptEnabled,
						...(this.getEndChatModalOnChatRestart && {
							clearConversation: true,
							hidePill: true,
							restartChat: true
						})
					})
	
					if (this.getEndChatModalOnChatRestart) {
						const eventData = {
							eventType: chatEvents.WIDGET_EVENT,
							subType: constantStrings.CHAT_RESTART
						}
						this.$store.dispatch('sendEvent', { eventData })
					}
	
					/**
					 * If either survey or end chat greeting is present then don't minimize CW
					 * In case of survey, wait for either skip or submit event for minimize CW
					 */
					isMinimizeValid && this.$store.dispatch('minimizeCW')
					emitEventToParentWindow(
						eventType.END_CHAT_CONFIRM,
						getEventDataPoints()
					)
					if (this.sendTranscriptCheckbox) {
						emitEventToParentWindow(
							eventType.END_CHAT_WITH_TRANSCRIPT,
							getEventDataPoints()
						)
					}
        }
      } catch (err) {
        console.log(err.message)
        new AppWarn({
          name: 'EndChat',
          message: `Error while ending chat. ${err.message}`
        })
      } finally {
        this.handleClose(false)
      }
    },
    handleClose(closeMenuWrapper, emitEvent = false) {
      this.isEventAnalyticsMP &&
        this.$segment.global.sendEvent('end_chat_cancel_click')
      if (this.getEndChatModalOnChatRestart)
        this.$store.dispatch('updateEndChatModalOnChatRestart', false)
      this.$store.dispatch('updateEndChatModalStatus', closeMenuWrapper)
      this.$nextTick(function () {
        document.getElementById('header-close-btn')?.focus()
      })
      emitEvent &&
        emitEventToParentWindow(eventType.END_CHAT_CANCEL, getEventDataPoints())
    },
    resetError() {
      this.error = {
        errorState: false,
        errorMessage: ''
      }
    },
    async downloadTranscript(url) {
      const response = await axios.get(url, {
        responseType: 'blob' // or 'arraybuffer' for binary
      })

      const blob = new Blob([response.data])
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      const currentDate = new Date()
      const transcriptFileName =
        'LatestTranscript ' +
        currentDate.getDate() +
        '_' +
        (currentDate.getMonth() + 1) +
        '_' +
        currentDate.getFullYear() +
        '__' +
        currentDate.getHours() +
        '_' +
        currentDate.getMinutes() +
        '_' +
        currentDate.getSeconds()
      link.download = `${transcriptFileName}.txt`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    async handleDownload() {
      try {
        if (!this.isDownloadInactive) return
        this.isEventAnalyticsMP &&
          this.$segment.global.sendEvent('end_chat_download_transcript')
        const startTime = new Date().getTime()
        this.downloadStatus = constantStrings.ACTIVE
        const transcript = await generateTranscript(false)
        const link = document.createElement('a')
        const file = new Blob([transcript], { type: 'text/plain' })
        link.href = URL.createObjectURL(file)
        const currentDate = new Date()
        const transcriptFileName =
          'Transcript ' +
          currentDate.getDate() +
          '_' +
          (currentDate.getMonth() + 1) +
          '_' +
          currentDate.getFullYear() +
          '__' +
          currentDate.getHours() +
          '_' +
          currentDate.getMinutes() +
          '_' +
          currentDate.getSeconds()
        link.download = `${transcriptFileName}.txt`
        link.click()
        URL.revokeObjectURL(link.href)
        // const { data } = await upload.getChatTranscript()
        // await this.downloadTranscript(data.chatTranscriptUrl)
        const endTime = new Date().getTime()
        setTimeout(
          () => {
            this.downloadStatus = constantStrings.COMPLETED
          },
          Math.max(1000 - (endTime - startTime), 0)
        )
        emitEventToParentWindow(
          eventType.DOWNLOAD_TRANSCRIPT,
          getEventDataPoints()
        )
      } catch (err) {
        this.downloadStatus = constantStrings.INACTIVE
        console.log(err.message)
        new AppWarn({
          name: 'EndChat',
          message: `Error while downloading transcript. ${err.message}`
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/styles/css/theme.scss';
@import '@/styles/css/commonStyles.scss';

.end-chat-wrapper {
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 2147483647;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.3);
  .end-chat-container {
    @include border-radius(var(--radius-chat-inner-component));
    background: #fff;
    padding: 16px;
    width: 100%;
    max-width: 606px;
    box-sizing: border-box;
    height: auto;
    box-shadow:
      0px 3.2px 7.2px 0px rgba(0, 0, 0, 0.13),
      0px 0.6px 1.8px 0px rgba(0, 0, 0, 0.4);
    &.end-chat-container-compact {
      position: absolute;
      bottom: 0;
      padding: 24px;
    }
    .close-icon-container {
      display: flex;
      justify-content: end;
      cursor: pointer;
    }
    .end-chat-text {
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      padding: 0;
      margin: 0;
      margin-left: 16px;
      &.end-chat-text-compact {
        margin-left: 0px;
      }
    }
    .disclaimer-text {
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      margin: 20px 16px 32px 16px;
      &.disclaimer-text-compact {
        margin: 8px 0px 24px 0px;
      }
    }
    .transcript-outer-container {
      margin-bottom: 20px;
      &.transcript-container-style {
        padding-left: 16px;
        padding-right: 16px;
      }
      .transcript-container-header {
        font-style: normal;
        font-weight: 600;
        line-height: 20px;
        margin-bottom: 8px;
      }
      .transcript-container-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .send-transcript-inner-container {
          .send-transcript-container-content {
            display: flex;
            padding: 8px 0px 8px 0px;
            justify-content: space-between;
            align-items: center;
            :deep(label) {
              color: #b0b0b0;
              background: green;
            }
          }
        }
        .download-transcript-outer-container {
          margin-left: -8px;
          .download-transcript-btn {
            position: relative;
            display: flex;
            align-items: center;
            background: none;
            border: none;
            gap: 8px;
            cursor: pointer;
            &:focus-visible {
              outline: $tab-focused-outline;
            }
            &.download-transcript-btn-disabled {
              cursor: default;
              p {
                color: #000;
              }
            }
            .inactive-icon {
              path {
                fill: var(--widget-primary-color);
              }
            }
            .active-icon {
              animation: spin 1s infinite linear;
            }
            .fade-enter-active,
            .fade-leave-active {
              transition: opacity 0.5s ease;
              .loader {
                animation: scaleOut 0.5s;
              }
            }
            .fade-enter-from,
            .fade-leave-to {
              opacity: 0;
            }
            @keyframes scaleOut {
              0% {
                transform: scale(1);
              }
              100% {
                transform: scale(0);
              }
            }
            @keyframes spin {
              0% {
                transform: rotate(0deg);
              }
              100% {
                transform: rotate(360deg);
              }
            }
            .submit-icon {
              display: flex;
              padding: 4px;
              justify-content: center;
              align-items: center;
              border-radius: 16px;
              background: #008a00;
              height: 16px;
              width: 16px;
              path {
                fill: '#FFFFFF';
              }
            }
            p {
              font-size: inherit;
              font-style: normal;
              font-weight: 400;
              line-height: 20px;
              color: var(--widget-primary-color);
              &.inactive-text {
                font-weight: 600;
              }
            }
            :deep(.loader) {
              width: 26px !important;
              height: 26px !important;
            }
          }
        }
      }
    }
    .custom-input-outer-wrapper {
      padding: 8px 0px 12px 0px;
    }
    .buttons-outer-container {
      display: flex;
      justify-content: end;
      gap: 16px;
      border-top: 1px solid#E8E8E8;
      padding: 16px 32px 8px 16px;
      margin-right: -32px;
      margin-left: -32px;
      &.buttons-outer-container-compact {
        display: flex;
        flex-direction: column-reverse;
        gap: 8px;
        border: none;
        padding: 0px;
        margin-right: 0;
        margin-left: 0;
      }
      .btn {
        font-size: inherit;
        font-weight: 600;
        line-height: 20px;
        padding: 9px 20px;
        height: 40px;
        min-width: 106px;
        display: flex;
        align-items: center;
        justify-content: center;
        @include border-radius(var(--radius-chat-inner-component));
        border: 1px solid var(--widget-primary-color);
      }
      .btn-disabled:hover {
        cursor: not-allowed !important;
      }
      .btn:hover {
        cursor: pointer;
      }
      .cancel-btn {
        background: #fff;
        border: none;
        color: var(--widget-primary-color);
        &:hover {
          background: var(--button-links-hover-background-color);
          color: var(--widget-primary-color);
        }
        &.cancel-btn-compact {
          width: 100%;
        }
        &.btn-disabled {
          color: #b0b0b0;
          &:hover {
            background: none;
          }
        }
      }
      .confirm-btn {
        background: var(--widget-primary-color);
        color: #fff;
        width: fit-content;
        padding: 0px 32px 0px 32px;
        &.confirm-btn-compact {
          width: 100%;
        }
        &.btn-disabled {
          background: #f2f2f2;
          color: #b0b0b0;
          border: none;
        }
      }
    }
  }
}
</style>
